/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import moment from "moment";
import GDPRApi from "../../../api/GDPRApi";
import { strings } from "../../../common/Strings/Strings";
import { LocalLanguageResponse } from "../../../models/language/LocalLanguageResponse";
import { PolicyResponse } from "../../../models/management/PolicyResponse";
import { getGeneralError } from "../../../util/helperFunctions";
import { PolicyType } from "../../../models/management/PolicyType";
import { CountrySettingsResponse } from "../../../models/management/CountrySettingsResponse";
import AddPolicyModal from "./AddPolicyModal";
import EditPolicyModal from "./EditPolicyModal";
import DeletePolicyModal from "./DeletePolicyModal";
import AlertBox from "../../../components/AlertBox";
import SimpleCheckBox from "../../../components/InputFieldsSimple/SimpleCheckBox";
import Button from "../../../components/Button";
import { Trash } from "../../../common/Icons/Trash";
import { PencilSquare } from "../../../common/Icons/PencilSquare";

interface Props {
  policyType: PolicyType;
  policyName: string;
  policies: PolicyResponse[];
  languages: LocalLanguageResponse[];
  country: CountrySettingsResponse;
  onPolicyChanged: (policy: PolicyResponse) => void;
  onPrimaryPolicyChanged: (policy: PolicyResponse) => void;
}

export const formatVersion = (v: string): string => {
  const currentLocale = moment.locale();
  moment.locale(strings.getLanguage());
  const versionStr = moment(v)
    .utc()
    .utcOffset(-1 * new Date().getTimezoneOffset())
    .format("YYYY-MMM-DD hh:mm:ss A");
  moment.locale(currentLocale);
  return versionStr;
};

const PolicyList: React.FC<Props> = ({
  policyType,
  policyName,
  policies,
  languages,
  country,
  onPolicyChanged,
  onPrimaryPolicyChanged,
}: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [selectedPolicyLanguageCode, setSelectedPolicyLanguageCode] = useState<string>();
  const [selectedPolicyStatement, setSelectedPolicyStatement] = useState<string>();
  const [selectedPolicyVersion, setSelectedPolicyVersion] = useState<string>();

  const [policyToDelete, setPolicyToDelete] = useState<PolicyResponse>();

  const openAddModal = () => {
    setShowAddModal(true);
    setError(null);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const openEditModal = (policy: PolicyResponse) => {
    setShowEditModal(true);
    setSelectedPolicyLanguageCode(policy.languageCode);
    setSelectedPolicyStatement(policy.statement);
    setSelectedPolicyVersion(formatVersion(policy.version));
    setError(null);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedPolicyLanguageCode(undefined);
    setSelectedPolicyStatement(undefined);
    setSelectedPolicyVersion(undefined);
  };

  const openDeleteModal = (policy: PolicyResponse) => {
    setShowDeleteModal(true);
    setPolicyToDelete(policy);
    setError(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setPolicyToDelete(undefined);
  };

  const setPolicyPrimary = async (policy: PolicyResponse) => {
    setLoading(true);
    onPrimaryPolicyChanged(policy);

    try {
      await GDPRApi.updatePrimaryPolicy(policy);
    } catch (e) {
      setError(await getGeneralError(e));
      onPolicyChanged(policy);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AlertBox message={error} />
      <div className="space-y-4">
        <div className="flex">
          <div className="font-bold">{policyName}</div>
          <div className="ml-auto">
            <Button variant="primary" onClick={openAddModal}>
              {strings.addNew}
            </Button>
          </div>
        </div>
        <table className="tw-table">
          <thead className="tw-thead">
            <tr>
              <th className="tw-th">{strings.language}</th>
              <th className="tw-th">{strings.primary}</th>
              <th />
            </tr>
          </thead>
          <tbody className="tw-tbody">
            {policies
              .filter((i) => i.countryCode === country.countryCode)
              .map((policy) => (
                <tr key={`${policy.countryCode}-${policy.languageCode}`}>
                  <td className="p-3">{policy.languageName}</td>
                  <td className="p-3">
                    <div className="flex justify-content-center mt-1">
                      <SimpleCheckBox
                        id={`${policy.countryCode}-${policy.languageCode}`}
                        name="primaryForCountry"
                        checked={policy.primaryForCountry}
                        onChange={(checked) => checked && setPolicyPrimary(policy)}
                      />
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      className="mr-3"
                      variant="icon"
                      onClick={() => {
                        openEditModal(policy);
                      }}
                      disabled={loading}
                    >
                      <PencilSquare />
                    </Button>
                    <Button
                      variant="icon"
                      onClick={() => {
                        openDeleteModal(policy);
                      }}
                      disabled={loading}
                    >
                      <Trash />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <AddPolicyModal
        country={country}
        languages={languages}
        onHide={closeAddModal}
        onPolicyChanged={onPolicyChanged}
        policies={policies}
        policyName={policyName}
        policyType={policyType}
        show={showAddModal}
      />
      <EditPolicyModal
        country={country}
        language={selectedPolicyLanguageCode}
        languages={languages}
        onHide={closeEditModal}
        onPolicyChanged={onPolicyChanged}
        policyName={policyName}
        policyType={policyType}
        setVersion={setSelectedPolicyVersion}
        show={showEditModal}
        statement={selectedPolicyStatement}
        version={selectedPolicyVersion}
      />
      <DeletePolicyModal
        onHide={closeDeleteModal}
        onPolicyChanged={onPolicyChanged}
        policyName={policyName}
        policyToDelete={policyToDelete}
        setError={setError}
        show={showDeleteModal}
      />
    </>
  );
};

export default PolicyList;
